/**
 * The language context, `t()`, and the direction switch.
 *
 * Direction is set on <html> rather than handled per component, because that is
 * what makes every CSS logical property flip at once. The whole stylesheet already
 * uses padding-inline / inset-inline-start / text-align: start, so switching to
 * Arabic needs no layout CSS at all -- which is exactly why that rule exists.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { DICTIONARIES, LANGS, RTL_LANGS } from "./dictionary";
import type { Keys, Lang } from "./dictionary";

const STORAGE_KEY = "petpoint-lang";

export function storedLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && (LANGS as string[]).includes(v) ? (v as Lang) : null;
  } catch {
    return null; // private mode
  }
}

/** The browser's preference, used only until the person chooses for themselves. */
function preferredLang(): Lang {
  const tags = navigator.languages ?? [navigator.language];
  for (const tag of tags) {
    if (tag?.toLowerCase().startsWith("ar")) return "ar";
    if (tag?.toLowerCase().startsWith("en")) return "en";
  }
  return "en";
}

export function applyDirection(lang: Lang) {
  const root = document.documentElement;
  root.setAttribute("lang", lang);
  root.setAttribute("dir", RTL_LANGS.has(lang) ? "rtl" : "ltr");
}

/** Substitutes {placeholders}. Missing values are left visible rather than blanked,
 *  so a broken interpolation shows up in review instead of shipping as a gap. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key) =>
    key in vars ? String(vars[key]) : whole,
  );
}

interface I18nState {
  lang: Lang;
  isRTL: boolean;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  t: (key: Keys, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<I18nState | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => storedLang() ?? preferredLang());

  useEffect(() => {
    applyDirection(lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode: the choice lasts for this page only */
    }
  }, []);

  const value = useMemo<I18nState>(() => {
    const dict = DICTIONARIES[lang];
    return {
      lang,
      isRTL: RTL_LANGS.has(lang),
      setLang,
      toggleLang: () => setLang(lang === "en" ? "ar" : "en"),
      // Falls back to English, then to the key itself. A visible key in the UI is a
      // far better failure than an empty element nobody notices.
      t: (key, vars) => interpolate(dict[key] ?? DICTIONARIES.en[key] ?? key, vars),
    };
  }, [lang, setLang]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n must be used inside an I18nProvider");
  return ctx;
}

/** Dates follow the chosen language, but DIGITS STAY WESTERN in both.
 *  Kuwaiti business documents and the invoices these modules read use 0-9, so
 *  localising numerals would make the screen disagree with the paper. */
export function formatDateTime(value: string | null | undefined, lang: Lang): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(lang === "ar" ? "ar-KW-u-nu-latn" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type { Keys, Lang };
